/*
 * Copyright (c) 2023. Collabro Limited
 */
import React, { useState } from "react";
import { FilterInputProps } from "./FilterScreen";
import {
  Alert,
  AlertColor,
  Collapse,
  IconButton,
  MenuItem,
  TextField,
} from "@mui/material";
import { Add, Close, Delete } from "@mui/icons-material";

export interface FilterProps {
  field: string;
  operator: string;
  value: string | number;
  type: string;
}

interface SelectValue {
  value: string;
  label: string;
}

interface AlertProps {
  open: boolean;
  message: string;
  severity?: AlertColor;
}

const FilterInput = (props: FilterInputProps) => {
  const [alert, setAlert] = useState<AlertProps>({ open: false, message: "" });
  const [currentFilter, setCurrentFilter] = useState<FilterProps>(
    props.filter ?? {
      field: "",
      operator: "",
      value: "",
      type: "",
    }
  );

  const [valueSelectOptions, setValueSelectOptions] = useState<SelectValue[]>(
    []
  );
  const [operatorOptions, setOperatorOptions] = useState<SelectValue[]>([
    {
      value: "=",
      label: "is",
    },
    {
      value: "like",
      label: "contains",
    },
  ]);
  const handleFieldSelect = (e: any) => {
    switch (e.target.value) {
      case "coreCrew":
      case "sex":
        setCurrentFilter({
          ...currentFilter,
          field: e.target.value,
          type: "boolean",
          operator: "",
          value: "",
        });
        if (e.target.value === "sex") {
          setValueSelectOptions([
            {
              value: "M",
              label: "Male",
            },
            {
              value: "F",
              label: "Female",
            },
          ]);
        } else {
          setValueSelectOptions([
            {
              value: "Y",
              label: "Yes",
            },
            {
              value: "N",
              label: "No",
            },
          ]);
        }
        setOperatorOptions([
          {
            value: "=",
            label: "is",
          },
        ]);
        break;
      case "personnelId":
        setCurrentFilter({
          ...currentFilter,
          field: e.target.value,
          type: "number",
          operator: "",
          value: "",
        });
        setOperatorOptions([
          {
            value: "=",
            label: "is",
          },
          {
            value: "like",
            label: "contains",
          },
        ]);
        break;
      default:
        setCurrentFilter({
          ...currentFilter,
          field: e.target.value,
          type: "text",
          operator: "",
          value: "",
        });
        setOperatorOptions([
          {
            value: "=",
            label: "is",
          },
          {
            value: "like",
            label: "contains",
          },
        ]);
    }
  };

  const handleAddButtonClicked = () => {
    if (currentFilter.type === "number" && !checkPatternIsValid()) {
      setAlert({
        open: true,
        message: "Value is not a number",
        severity: "warning",
      });
      return;
    }
    if (!checkFilterValuesAreValid()) {
      setAlert({ open: true, message: "Filter parameters are not valid" });
      return;
    }
    if (!checkMaxFilters()) {
      setAlert({ open: true, message: "Maximum 5 filters allowed" });
      return;
    }
    if (filterAlreadyExists()) {
      setAlert({
        open: true,
        message: "Filter already exists",
        severity: "warning",
      });
      return;
    }
    props.setExistingFilters([...props.existingFilters, currentFilter]);
    setAlert({ ...alert, open: false });
    setCurrentFilter({
      field: "",
      operator: "",
      value: "",
      type: "",
    });
  };

  const checkFilterValuesAreValid = (): boolean => {
    return !!(
      currentFilter.field &&
      currentFilter.operator &&
      currentFilter.value
    );
  };

  const checkPatternIsValid = () => {
    const value: HTMLInputElement | null = document.getElementById(
      "filter-value"
    ) as HTMLInputElement;
    return value.validity.valid;
  };

  const filterAlreadyExists = (): boolean => {
    return props.existingFilters.some(
      (filter) => filter.field === currentFilter.field
    );
  };

  const handleOperatorChange = (e: any) => {
    setCurrentFilter({ ...currentFilter, operator: e.target.value });
  };

  const handleValueChange = (e: any) => {
    setCurrentFilter({ ...currentFilter, value: e.target.value });
  };

  const checkMaxFilters = (): boolean => {
    return props.existingFilters.length < 5;
  };

  const handleFilterDelete = () => {
    props.setExistingFilters(
      props.existingFilters.filter(
        (filter) => filter.field !== props.filter!.field
      )
    );
  };

  return (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          padding: 5,
          marginBottom: 10,
          justifyContent: "space-between",
        }}
      >
        <TextField
          variant="standard"
          size="small"
          select
          disabled={!!props.filter}
          value={currentFilter.field}
          onChange={handleFieldSelect}
          label="Field"
          sx={{ minWidth: 130, maxWidth: 130, mr: 1 }}
        >
          {props.options.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          variant="standard"
          size="small"
          select
          value={currentFilter.operator}
          onChange={handleOperatorChange}
          disabled={!currentFilter.field || !!props.filter}
          label="Operator"
          sx={{ minWidth: 130, maxWidth: 130, mr: 1 }}
        >
          {operatorOptions.map((operator) => (
            <MenuItem key={operator.value} value={operator.value}>
              {operator.label}
            </MenuItem>
          ))}
        </TextField>
        {!props.filter && currentFilter.type === "boolean" ? (
          <TextField
            variant="standard"
            size="small"
            select
            value={currentFilter.value}
            onChange={handleValueChange}
            disabled={!currentFilter.operator || !!props.filter}
            label="Value"
            fullWidth
          >
            {valueSelectOptions.map((value) => (
              <MenuItem key={value.value} value={value.value}>
                {value.label}
              </MenuItem>
            ))}
          </TextField>
        ) : (
          <TextField
            type="text"
            disabled={!currentFilter.operator || !!props.filter}
            variant="standard"
            inputProps={{
              id: "filter-value",
              maxLength: 50,
              inputMode: currentFilter.type === "number" ? "numeric" : "text",
              pattern: currentFilter.type === "number" ? "[0-9]*" : "",
            }}
            value={currentFilter.value}
            onChange={handleValueChange}
            size="small"
            label="Value"
            fullWidth
          />
        )}
        {props.filter ? (
          <IconButton onClick={handleFilterDelete}>
            <Delete />
          </IconButton>
        ) : (
          <IconButton onClick={handleAddButtonClicked}>
            <Add />
          </IconButton>
        )}
      </div>
      <Collapse in={alert.open}>
        <Alert
          action={
            <IconButton
              color="inherit"
              size="small"
              onClick={() => {
                setAlert({ ...alert, open: false });
              }}
            >
              <Close fontSize="inherit" />
            </IconButton>
          }
          severity={alert.severity ?? "error"}
        >
          {alert.message}
        </Alert>
      </Collapse>
    </>
  );
};

export default FilterInput;
